/**
 * THE ONE PLACE THE LIVE-ALERT PALETTE IS WRITTEN DOWN (owner, 2026-09-09).
 *
 * The landmine and competitor alerts are the only two surfaces in the product a
 * rep reads DURING a live call, with a customer waiting. That is the whole
 * design constraint: they are glanced at, not studied, so everything on them
 * has to earn its place, and the two must be distinguishable in peripheral
 * vision before a single word is read.
 *
 * WHY TOKENS RATHER THAN TAILWIND CLASSES. These exact colours are mirrored by
 * the macOS and WPF desktop apps, which cannot import a Tailwind config. A hex
 * value written down once here is a value the other two surfaces can copy
 * verbatim and a reviewer can diff; a `bg-red-50` scattered through JSX is not.
 * Layout and typography stay in Tailwind — only the themed colours live here.
 *
 * The app's own surface is white, so NEITHER CARD IS WHITE: each sits on a
 * faint tint of its own accent, which is what separates the alert from the page
 * without a heavy border or a shadow.
 */

export interface AlertPalette {
  /** Card fill. Deliberately not white — the page behind it already is. */
  surface: string;
  /** Hairline around the card. */
  border: string;
  /** The 3px spine down the left edge, and the colour of section rules. */
  accent: string;
  /** Badge fill. Solid, high contrast — this is the peripheral-vision cue. */
  badge: string;
  /** Badge text. */
  badgeText: string;
  /** Section heading text. */
  heading: string;
  /** Body text. */
  body: string;
  /** Muted text: the trigger quote and the speaker line. */
  muted: string;
  /** Divider between sections inside the card. */
  divider: string;
}

/** Red. A landmine is a thing you must not walk into. */
export const LANDMINE_PALETTE: AlertPalette = {
  surface: '#FFF7F7',
  border: '#F5D2D2',
  accent: '#DC2626',
  badge: '#DC2626',
  badgeText: '#FFFFFF',
  heading: '#7F1D1D',
  body: '#3F3F46',
  muted: '#78716C',
  divider: '#F3DCDC',
};

/** Purple. A competitor is not a hazard, it is a different conversation. */
export const COMPETITOR_PALETTE: AlertPalette = {
  surface: '#FAF8FF',
  border: '#DDD3F5',
  accent: '#7C3AED',
  badge: '#7C3AED',
  badgeText: '#FFFFFF',
  heading: '#4C1D95',
  body: '#3F3F46',
  muted: '#78716C',
  divider: '#E7DEFA',
};

export const ALERT_BADGE_LABEL = {
  landmine: 'LANDMINE',
  competitor: 'COMPETITOR',
} as const;

/** Who is looking at a live alert (SessionViewerContext `useAlertViewer`). */
export interface AlertViewer {
  /** The viewer's own participant (a co-seller's `salesperson_<n>`, the bot owner's "Me"); null for a watcher. */
  ownParticipantId: string | null;
  /** The viewer runs the call: the bot's owner, or the page with no call at all. */
  isCallOwner: boolean;
}

/** The fields of a landmine, competitor or speech-rate frame that say who raised it. */
export interface AlertRaiser {
  speaker?: string | null;
  participantId?: string | number | null;
  speakerName?: string | null;
}

export const ALERT_SPEAKER_COPY = {
  youSaid: 'You said',
  customerSaid: 'Customer said',
  salespersonSaid: 'Salesperson said',
  said: 'Said',
  nameSaid: (speakerName: string): string => `${speakerName} said`,
  you: 'You',
  customer: 'Customer',
  salesperson: 'Salesperson',
} as const;

/**
 * Was this raised by the person looking at it? Every alert goes to everyone in
 * the room, unfiltered (owner rule), so the label is the only thing that says
 * whose it is. By participant: the frame's `participantId` is the viewer's own.
 * A frame that names nobody (an older service) is the call owner's own when a
 * salesperson raised it, which is all "You said" meant before co-sellers. A
 * watcher has no own participant and runs nothing, so it is never "You".
 */
export function isViewersOwnAlert(raiser: AlertRaiser | null | undefined, viewer: AlertViewer): boolean {
  const participantId = raiser?.participantId;
  if (participantId !== undefined && participantId !== null && String(participantId) !== '') {
    return viewer.ownParticipantId !== null && String(participantId) === viewer.ownParticipantId;
  }
  return viewer.isCallOwner && raiser?.speaker === 'salesperson';
}

/**
 * Who said the thing that set the alert off: "You said" for the viewer's own,
 * otherwise the raiser's name ("Dana said"), falling back to the role. The rep
 * needs this before anything else: their own slip and someone else's remark
 * call for opposite reactions.
 */
export function speakerLabel(raiser: AlertRaiser | null | undefined, viewer: AlertViewer): string {
  if (isViewersOwnAlert(raiser, viewer)) return ALERT_SPEAKER_COPY.youSaid;
  const speakerName = raiser?.speakerName?.trim();
  if (speakerName) return ALERT_SPEAKER_COPY.nameSaid(speakerName);
  if (raiser?.speaker === 'customer') return ALERT_SPEAKER_COPY.customerSaid;
  if (raiser?.speaker === 'salesperson') return ALERT_SPEAKER_COPY.salespersonSaid;
  return ALERT_SPEAKER_COPY.said;
}
